import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["id"],
  _excluded2 = ["locale"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import Button from '@atlaskit/button/standard-button';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { ErrorMessage, Field, Fieldset, RequiredAsterisk } from '@atlaskit/form';
import Link from '@atlaskit/link';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import SectionMessage from '@atlaskit/section-message';
import Select from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import { N300 } from '@atlaskit/theme/colors';
import { messages } from '../messages';
import { IntlProviderWithResolvedMessages } from './IntlProviderWithResolvedMessages';
var LinkWrapper = function LinkWrapper(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement("span", {
    style: {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      paddingInlineStart: "var(--ds-space-300, 24px)",
      // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
      marginInlineStart: "var(--ds-space-050, 4px)"
    }
  }, children);
};
var FeedbackForm = function FeedbackForm(_ref2) {
  var _ref2$showTypeField = _ref2.showTypeField,
    showTypeField = _ref2$showTypeField === void 0 ? true : _ref2$showTypeField,
    _ref2$showDefaultText = _ref2.showDefaultTextFields,
    showDefaultTextFields = _ref2$showDefaultText === void 0 ? true : _ref2$showDefaultText,
    customContent = _ref2.customContent,
    onClose = _ref2.onClose,
    onSubmit = _ref2.onSubmit,
    feedbackTitle = _ref2.feedbackTitle,
    feedbackTitleDetails = _ref2.feedbackTitleDetails,
    feedbackGroupLabels = _ref2.feedbackGroupLabels,
    summaryPlaceholder = _ref2.summaryPlaceholder,
    canBeContactedLabel = _ref2.canBeContactedLabel,
    canBeContactedLink = _ref2.canBeContactedLink,
    enrolInResearchLabel = _ref2.enrolInResearchLabel,
    enrolInResearchLink = _ref2.enrolInResearchLink,
    submitButtonLabel = _ref2.submitButtonLabel,
    cancelButtonLabel = _ref2.cancelButtonLabel,
    anonymousFeedback = _ref2.anonymousFeedback,
    hasDescriptionDefaultValue = _ref2.hasDescriptionDefaultValue,
    selectLabel = _ref2.selectLabel,
    customTextAreaLabel = _ref2.customTextAreaLabel,
    _ref2$customFeedbackO = _ref2.customFeedbackOptions,
    customFeedbackOptions = _ref2$customFeedbackO === void 0 ? [] : _ref2$customFeedbackO,
    shouldReturnFocusRef = _ref2.shouldReturnFocusRef,
    disableSubmitButton = _ref2.disableSubmitButton;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    canBeContacted = _useState2[0],
    setCanBeContacted = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    description = _useState4[0],
    setDescription = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    enrollInResearchGroup = _useState6[0],
    setEnrollInResearchGroup = _useState6[1];
  var _useState7 = useState('empty'),
    _useState8 = _slicedToArray(_useState7, 2),
    type = _useState8[0],
    setType = _useState8[1];
  var _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isSubmitting = _useState0[0],
    setIsSubmitting = _useState0[1];
  var _useState1 = useState({}),
    _useState10 = _slicedToArray(_useState1, 2),
    validationErrors = _useState10[0],
    setValidationErrors = _useState10[1];
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var isTypeSelected = function isTypeSelected() {
    return type !== 'empty';
  };
  var canShowTextField = isTypeSelected() || !showTypeField;
  var hasDescription = description || hasDescriptionDefaultValue;

  // Feature flag determines validation behavior
  var useNewValidation = fg('feedback-collector-custom-validation');
  var isDisabled = useNewValidation ? isSubmitting || disableSubmitButton // New: only disable when submitting or explicitly disabled
  : disableSubmitButton || (showTypeField ? !isTypeSelected() || !hasDescription : !hasDescription); // Old: disable based on form validation

  var getValidationErrors = function getValidationErrors() {
    var errors = {};

    // Validate type selection if showTypeField is true
    if (showTypeField && !isTypeSelected()) {
      errors.type = formatMessage(messages.validationErrorTypeRequired);
    }

    // Validate description if showDefaultTextFields is true
    if (showDefaultTextFields && !hasDescription) {
      errors.description = formatMessage(messages.validationErrorDescriptionRequired);
    }
    return errors;
  };
  var getFieldLabels = function getFieldLabels(record) {
    var _record$bug, _record$comment, _record$suggestion, _record$question, _record$empty, _record$not_relevant, _record$not_accurate, _record$too_slow, _record$unhelpful_lin, _record$other;
    return {
      bug: (record === null || record === void 0 || (_record$bug = record.bug) === null || _record$bug === void 0 ? void 0 : _record$bug.fieldLabel) || formatMessage(messages.formBugLabel),
      comment: (record === null || record === void 0 || (_record$comment = record.comment) === null || _record$comment === void 0 ? void 0 : _record$comment.fieldLabel) || formatMessage(messages.formCommentLabel),
      suggestion: (record === null || record === void 0 || (_record$suggestion = record.suggestion) === null || _record$suggestion === void 0 ? void 0 : _record$suggestion.fieldLabel) || formatMessage(messages.formSuggestionLabel),
      question: (record === null || record === void 0 || (_record$question = record.question) === null || _record$question === void 0 ? void 0 : _record$question.fieldLabel) || formatMessage(messages.formQuestionLabel),
      empty: (record === null || record === void 0 || (_record$empty = record.empty) === null || _record$empty === void 0 ? void 0 : _record$empty.fieldLabel) || formatMessage(messages.formEmptyLabel),
      not_relevant: (record === null || record === void 0 || (_record$not_relevant = record.not_relevant) === null || _record$not_relevant === void 0 ? void 0 : _record$not_relevant.fieldLabel) || formatMessage(messages.formNotRelevantLabel),
      not_accurate: (record === null || record === void 0 || (_record$not_accurate = record.not_accurate) === null || _record$not_accurate === void 0 ? void 0 : _record$not_accurate.fieldLabel) || formatMessage(messages.formNotAccurateLabel),
      too_slow: (record === null || record === void 0 || (_record$too_slow = record.too_slow) === null || _record$too_slow === void 0 ? void 0 : _record$too_slow.fieldLabel) || formatMessage(messages.formTooSlowLabel),
      unhelpful_links: (record === null || record === void 0 || (_record$unhelpful_lin = record.unhelpful_links) === null || _record$unhelpful_lin === void 0 ? void 0 : _record$unhelpful_lin.fieldLabel) || formatMessage(messages.formUnhelpfulLinksLabel),
      other: (record === null || record === void 0 || (_record$other = record.other) === null || _record$other === void 0 ? void 0 : _record$other.fieldLabel) || formatMessage(messages.formOtherLabel)
    };
  };
  var getSelectOptions = function getSelectOptions(record) {
    var _record$question2, _record$comment2, _record$bug2, _record$suggestion2;
    return [{
      label: (record === null || record === void 0 || (_record$question2 = record.question) === null || _record$question2 === void 0 ? void 0 : _record$question2.selectOptionLabel) || formatMessage(messages.selectionOptionQuestionLabel),
      value: 'question'
    }, {
      label: (record === null || record === void 0 || (_record$comment2 = record.comment) === null || _record$comment2 === void 0 ? void 0 : _record$comment2.selectOptionLabel) || formatMessage(messages.selectionOptionCommentLabel),
      value: 'comment'
    }, {
      label: (record === null || record === void 0 || (_record$bug2 = record.bug) === null || _record$bug2 === void 0 ? void 0 : _record$bug2.selectOptionLabel) || formatMessage(messages.selectionOptionBugLabel),
      value: 'bug'
    }, {
      label: (record === null || record === void 0 || (_record$suggestion2 = record.suggestion) === null || _record$suggestion2 === void 0 ? void 0 : _record$suggestion2.selectOptionLabel) || formatMessage(messages.selectionOptionSuggestionLabel),
      value: 'suggestion'
    }];
  };
  var getDefaultPlaceholder = function getDefaultPlaceholder(record) {
    var _record$empty2;
    return (record === null || record === void 0 || (_record$empty2 = record.empty) === null || _record$empty2 === void 0 ? void 0 : _record$empty2.selectOptionLabel) || formatMessage(messages.selectionOptionDefaultPlaceholder);
  };
  var focusRef = useRef();
  var selectOptions = customFeedbackOptions.length > 0 ? customFeedbackOptions : getSelectOptions(feedbackGroupLabels);
  var renderContactLabelAppify = function renderContactLabelAppify() {
    if (fg('jfp_a11y_team_feedback_collector_nested_elements')) {
      return messages.canBeContactedLabelAppifyWithoutLink;
    }
    return messages.canBeContactedLabelAppify;
  };
  var renderContactLabel = function renderContactLabel() {
    if (fg('jfp_a11y_team_feedback_collector_nested_elements')) {
      return messages.canBeContactedLabelWithoutLink;
    }
    return messages.canBeContactedLabel;
  };
  return /*#__PURE__*/React.createElement(Modal, {
    shouldCloseOnOverlayClick: false,
    onClose: onClose,
    testId: "feedbackCollectorModalDialog",
    shouldReturnFocus: shouldReturnFocusRef,
    shouldScrollInViewport: true
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var errors;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!useNewValidation) {
              _context.next = 5;
              break;
            }
            // New validation: validate on submit and show errors
            errors = getValidationErrors(); // If there are validation errors, show them and don't submit
            if (!(Object.keys(errors).length > 0)) {
              _context.next = 5;
              break;
            }
            setValidationErrors(errors);
            return _context.abrupt("return");
          case 5:
            // Submit the form (both old and new validation paths reach here)
            setIsSubmitting(true);
            _context.prev = 6;
            _context.next = 9;
            return onSubmit({
              canBeContacted: canBeContacted,
              description: description,
              enrollInResearchGroup: enrollInResearchGroup,
              type: type
            });
          case 9:
            _context.prev = 9;
            setIsSubmitting(false);
            return _context.finish(9);
          case 12:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[6,, 9, 12]]);
    }))
  }, function (_ref4) {
    var formProps = _ref4.formProps;
    return /*#__PURE__*/React.createElement("form", formProps, /*#__PURE__*/React.createElement(ModalHeader, {
      hasCloseButton: true
    }, /*#__PURE__*/React.createElement(ModalTitle, null, feedbackTitle || /*#__PURE__*/React.createElement(FormattedMessage, messages.feedbackTitle))), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement("p", {
      style: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
        color: "var(--ds-text-subtle, ".concat(N300, ")"),
        // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
        marginBottom: "var(--ds-space-300, 24px)"
      }
    }, formatMessage(messages.requiredFieldsSummary), /*#__PURE__*/React.createElement(RequiredAsterisk, null)), feedbackTitleDetails, customContent, showTypeField ? /*#__PURE__*/React.createElement(Field, {
      name: "topic",
      label: selectLabel || formatMessage(messages.selectionOptionDefaultLabel),
      isRequired: true,
      "aria-required": true // JCA11Y-1619
    }, function (_ref5) {
      var _ref5$fieldProps = _ref5.fieldProps,
        id = _ref5$fieldProps.id,
        restProps = _objectWithoutProperties(_ref5$fieldProps, _excluded);
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Select, _extends({}, restProps, {
        onChange: function onChange(option) {
          if (!option || option instanceof Array) {
            return;
          }
          setType(option.value);
          // Clear validation error when user selects a type (only for new validation)
          if (useNewValidation && validationErrors.type) {
            setValidationErrors(function (prev) {
              return _objectSpread(_objectSpread({}, prev), {}, {
                type: undefined
              });
            });
          }
        },
        menuPortalTarget: document.body,
        styles: {
          menuPortal: function menuPortal(base) {
            return _objectSpread(_objectSpread({}, base), {}, {
              zIndex: 9999
            });
          }
        },
        options: selectOptions
        // @ts-ignore
        ,
        ref: focusRef,
        placeholder: getDefaultPlaceholder(feedbackGroupLabels),
        inputId: id
      })), useNewValidation && validationErrors.type && /*#__PURE__*/React.createElement(ErrorMessage, null, validationErrors.type));
    }) : null, showDefaultTextFields && canShowTextField && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Field, {
      label: showTypeField ? getFieldLabels(feedbackGroupLabels)[type] : customTextAreaLabel || formatMessage(messages.defaultCustomTextAreaLabel),
      isRequired: true,
      name: "description"
    }, function (_ref6) {
      var fieldProps = _ref6.fieldProps;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextArea, _extends({}, fieldProps, {
        name: "foo",
        minimumRows: 6,
        placeholder: summaryPlaceholder || undefined,
        onChange: function onChange(e) {
          setDescription(e.target.value);
          // Clear validation error when user types
          if (useNewValidation && validationErrors.description) {
            setValidationErrors(function (prev) {
              return _objectSpread(_objectSpread({}, prev), {}, {
                description: undefined
              });
            });
          }
        },
        value: description
      })), useNewValidation && validationErrors.description && /*#__PURE__*/React.createElement(ErrorMessage, null, validationErrors.description));
    }), !anonymousFeedback && /*#__PURE__*/React.createElement(Fieldset, null, /*#__PURE__*/React.createElement("legend", {
      "aria-hidden": false,
      hidden: true
    }, "Atlassian opt-in options"), /*#__PURE__*/React.createElement(Field, {
      name: "can-be-contacted"
    }, function (_ref7) {
      var fieldProps = _ref7.fieldProps;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Checkbox, _extends({}, fieldProps, {
        "aria-describedby": undefined // JCA11Y-1988
        ,
        label: canBeContactedLabel || /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, fg('product-terminology-refresh') ? renderContactLabelAppify() : renderContactLabel(), !fg('jfp_a11y_team_feedback_collector_nested_elements') && {
          values: {
            a: function a(chunks) {
              return /*#__PURE__*/React.createElement(Link, {
                href: "https://www.atlassian.com/legal/privacy-policy",
                target: "_blank"
              }, chunks);
            }
          }
        })),
        onChange: function onChange(event) {
          return setCanBeContacted(event.target.checked);
        }
      })), canBeContactedLabel && canBeContactedLink && fg('jfp_a11y_team_feedback_collector_nested_elements') && /*#__PURE__*/React.createElement(LinkWrapper, null, canBeContactedLink), !canBeContactedLabel && fg('jfp_a11y_team_feedback_collector_nested_elements') && /*#__PURE__*/React.createElement(LinkWrapper, null, /*#__PURE__*/React.createElement(Link, {
        href: "https://www.atlassian.com/legal/privacy-policy",
        target: "_blank"
      }, formatMessage(messages.privacyPolicy))));
    }), /*#__PURE__*/React.createElement(Field, {
      name: "enroll-in-research-group"
    }, function (_ref8) {
      var fieldProps = _ref8.fieldProps;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Checkbox, _extends({}, fieldProps, {
        "aria-describedby": undefined // JCA11Y-1988
        ,
        label: enrolInResearchLabel || formatMessage(messages.enrolInResearchLabel),
        onChange: function onChange(event) {
          return setEnrollInResearchGroup(event.target.checked);
        }
      })), enrolInResearchLabel && enrolInResearchLink && fg('jfp_a11y_team_feedback_collector_nested_elements') && /*#__PURE__*/React.createElement(LinkWrapper, null, enrolInResearchLink));
    })) || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Field, {
      name: 'anonymousFeedback'
    }, function () {
      return /*#__PURE__*/React.createElement(SectionMessage, {
        title: formatMessage(messages.feedbackIsAnonymousTitle),
        appearance: 'information'
      }, /*#__PURE__*/React.createElement("p", null, formatMessage(messages.feedbackIsAnonymous)));
    })))), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
      appearance: "subtle",
      onClick: onClose
    }, cancelButtonLabel || /*#__PURE__*/React.createElement(FormattedMessage, messages.cancelButtonLabel)), /*#__PURE__*/React.createElement(Button, {
      appearance: "primary",
      type: "submit",
      isDisabled: isSubmitting || isDisabled,
      testId: "feedbackCollectorSubmitBtn"
    }, submitButtonLabel || /*#__PURE__*/React.createElement(FormattedMessage, messages.submitButtonLabel))));
  }));
};
var FeedbackFormWithIntl = function FeedbackFormWithIntl(_ref9) {
  var locale = _ref9.locale,
    props = _objectWithoutProperties(_ref9, _excluded2);
  return /*#__PURE__*/React.createElement(IntlProviderWithResolvedMessages, {
    locale: locale
  }, /*#__PURE__*/React.createElement(FeedbackForm, props));
};
export default FeedbackFormWithIntl;